import { CreatePresetRequestDTO, PresetContentType, PresetDTO } from "@/api/dto/presets"
import { DataSource } from "@/store/data-source"
import {
	deletePreset_deprecated,
	fetchPreset,
	fetchPresets_deprecated,
	PresetRecord,
	updatePreset,
} from "@/storage/indexeddb"
import { PNApiOperations } from "@/api/operations"
import { fetchAllPages } from "@/api/paginate"
import { Adjustments } from "@/core/types"
import { queryClient } from "@/api/constants"

export interface PresetItem {
	id: string
	createdAt: Date
	updatedAt: Date
	name: string
	orderScore: number
	schemaVersion: number
	data?: File | string
	adjustments?: Partial<Adjustments>
	contentUrl?: string
	contentType?: PresetContentType
	contentDigest?: string
	source: DataSource
	group?: string
	readOnly?: boolean
	lutUrl?: string
}

export class PresetService {
	static prefetchPresets = async (): Promise<void> => {
		await queryClient.prefetchQuery({
			queryKey: ["presets"],
			queryFn: PresetService.listPresets,
			staleTime: 60000,
		})
	}
	static listPresets = async () => {
		const presets: PresetItem[] = []
		const backendPresets = await fetchAllPages(PNApiOperations.listPresets)
		for (const dto of backendPresets) {
			presets.push(PresetService.dtoToPreset(dto))
		}

		const indexedDBPresets = await fetchPresets_deprecated()
		for (const record of indexedDBPresets) {
			presets.push(PresetService.recordToPreset(record))
		}
		presets.sort((a, b) => b.orderScore - a.orderScore)
		return presets
	}

	static createPreset = async (body: CreatePresetRequestDTO) => {
		let dto: PresetDTO
		try {
			dto = await PNApiOperations.createPreset(body)
		} catch (err: any) {
			if (err && err.response && err.response.status === 409) {
				const existingPresets = await PresetService.listPresets()
				if (
					!existingPresets.find(
						(preset) =>
							preset.id === body.presetId ||
							preset.contentDigest === body.contentDigest,
					)
				) {
					// This preset is owned by another account
					const { presetId: _presetId, ...bodyWithoutId } = body
					dto = await PNApiOperations.createPreset(bodyWithoutId)
				} else {
					throw err
				}
			} else {
				throw err
			}
		}
		return PresetService.dtoToPreset(dto)
	}

	static updatePreset = async (
		options: {
			id: string
			source: DataSource
		},
		mutations: Partial<PresetItem>,
	) => {
		if (!options.id || !options.source) {
			throw new Error("Cannot update preset without a target ID or source.")
		}

		if (options.source === DataSource.Backend) {
			const dto = await PNApiOperations.updatePreset(options.id, mutations)
			return PresetService.dtoToPreset(dto)
		} else {
			const existingPresetRecord = await fetchPreset(options.id)
			if (!existingPresetRecord) {
				throw new Error(
					`Trying to update an indexedDB preset that doesn't exist with id ${options.id}`,
				)
			}
			for (const key in mutations) {
				if (mutations[key] !== undefined) {
					if (key === "orderScore" && mutations.orderScore) {
						existingPresetRecord.createdAt = new Date(Math.floor(mutations.orderScore))
					} else {
						existingPresetRecord[key] = mutations[key]
					}
				}
			}
			await updatePreset(existingPresetRecord)
			return PresetService.recordToPreset(existingPresetRecord)
		}
	}

	static deletePreset = async (options: { id: string; source: DataSource }) => {
		if (!options.id) {
			throw new Error("Cannot delete preset")
		}
		if (options.source === DataSource.Backend) {
			await PNApiOperations.deletePreset(options.id)
		}
		await deletePreset_deprecated({ id: options.id })
	}

	static recordToPreset = (record: PresetRecord): PresetItem => {
		return {
			id: record.id,
			createdAt: record.createdAt,
			updatedAt: record.updatedAt,
			name: record.name,
			orderScore: record.createdAt.getTime() / 1000,
			schemaVersion: 1,
			data: record.data,
			adjustments: record.adjustments,
			source: DataSource.IndexedDB,
			group: record.folder,
		}
	}

	static dtoToPreset = (dto: PresetDTO): PresetItem => {
		return {
			id: dto.presetId,
			createdAt: new Date(dto.createdAt),
			updatedAt: new Date(dto.updatedAt),
			name: dto.name,
			orderScore: dto.orderScore,
			schemaVersion: dto.schemaVersion,
			contentUrl: dto.contentUrl,
			contentType: dto.contentType,
			contentDigest: dto.contentDigest,
			source: DataSource.Backend,
			group: dto.group,
			lutUrl: dto.lutUrl,
		}
	}
}
